<template>
  <Teleport to="body">
    <Transition name="mobile-panel">
      <div v-if="open" class="mobile-panel-layer">
        <button
          class="mobile-panel-backdrop"
          type="button"
          :aria-label="closeLabel"
          @click="$emit('close')"
          @wheel.prevent
          @touchmove.prevent
        ></button>
        <aside
          class="mobile-panel"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel"
        >
          <header class="mobile-panel__header">
            <slot name="header"></slot>
            <button
              ref="closeButtonRef"
              class="mobile-panel__close"
              type="button"
              :aria-label="closeLabel"
              @click="$emit('close')"
            >
              <CustomImg :src="closeIcon" alt="" />
            </button>
          </header>
          <div class="mobile-panel__body">
            <slot></slot>
          </div>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import CustomImg from '@/components/CustomImg.vue';
import closeIcon from '@/assets/images/ai/cls.svg';

const props = withDefaults(defineProps<{
  open: boolean;
  ariaLabel?: string;
  closeLabel?: string;
}>(), {
  ariaLabel: 'Menu',
  closeLabel: 'Close',
});

const emit = defineEmits<{
  close: [];
}>();

const closeButtonRef = ref<HTMLButtonElement | null>(null);
let previousFocus: HTMLElement | null = null;
let desktopMedia: MediaQueryList | null = null;

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') emit('close');
};

const closeOnDesktop = (event: MediaQueryListEvent) => {
  if (event.matches && props.open) emit('close');
};

watch(() => props.open, async (open) => {
  if (open) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.addEventListener('keydown', handleKeydown);
    await nextTick();
    closeButtonRef.value?.focus();
    return;
  }

  document.removeEventListener('keydown', handleKeydown);
  previousFocus?.focus();
  previousFocus = null;
});

onMounted(() => {
  desktopMedia = window.matchMedia('(min-width: 901px)');
  desktopMedia.addEventListener('change', closeOnDesktop);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  desktopMedia?.removeEventListener('change', closeOnDesktop);
});
</script>

<style scoped lang="scss">
.mobile-panel-layer {
  position: fixed;
  inset: 0;
  z-index: 100;
}

.mobile-panel-backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: rgba(0, 0, 0, .48);
  cursor: default;
}

.mobile-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(80vw, 320px);
  height: 100%;
  color: #222;
  background: #fff;
  box-shadow: 5px 0 20px rgba(0, 0, 0, .12);
}

.mobile-panel__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  min-height: 48px;
  padding: 6px 10px;
}

.mobile-panel__close {
  display: grid;
  width: 36px;
  height: 36px;
  margin-left: auto;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  place-items: center;

  img { width: 18px; }
}

.mobile-panel__body {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.mobile-panel-enter-active,
.mobile-panel-leave-active {
  transition: opacity .22s ease;

  .mobile-panel { transition: transform .22s ease; }
}

.mobile-panel-enter-from,
.mobile-panel-leave-to {
  opacity: 0;

  .mobile-panel { transform: translateX(-100%); }
}

@media (min-width: 901px) {
  .mobile-panel-layer { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .mobile-panel-enter-active,
  .mobile-panel-leave-active,
  .mobile-panel-enter-active .mobile-panel,
  .mobile-panel-leave-active .mobile-panel { transition: none; }
}
</style>
